import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  from,
  HttpLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { ApolloProvider } from "@apollo/client/react";
import HelloSign from "hellosign-embedded";

import "./App.css";
import { Shell } from "@shared/components/Shell";
import { ToastProvider } from "@shared/components/Toast";
import { ThemeProvider, AuthProvider } from "@shared/context";
import { apolloURL, adminApolloURL } from "./constants/apollo.constants";
import { RoleRoute } from "./routes";
import { RealtimeProvider } from "./context/RealtimeContext";

// Eagerly loaded: auth pages (first interaction)
import Login from "./components/login/login";
import Register from "./components/register/register";
import ForgotPassword from "./components/forgot-password/forgot-password.component";
import ResetPassword from "./components/reset-password/reset-password.component";
import LockedPage from "./components/locked-page/locked-page.component";
import AuthCallback from "./pages/auth/callback";

// Lazy loaded: authenticated portal pages (code-splitting for bundle size)
const Overview = lazy(() => import("./components/overview/overview"));
const AccountDetails = lazy(() => import("./components/account-details/account-details"));
const TransactionsHistory = lazy(() => import("./components/transcations-history/transactions-history"));
const BillingPage = lazy(() => import("./pages/billing"));
const Checkout = lazy(() => import("./components/checkout/checkout"));
const CertificateType = lazy(() => import("./components/certificate-type/certificate-type"));
const MainRegistrationPage = lazy(() => import("./components/resitration-steps/main/main-registration-page"));
const UserInfo = lazy(() => import("./components/resitration-steps/user-info/user-info"));
const PersonalDetails = lazy(() => import("./components/resitration-steps/personal-details/personal-details"));
const Confirmation = lazy(() => import("./components/resitration-steps/confirmation/confirmation"));
const CreateTransaction = lazy(() => import("./components/create-transaction/create-transaction"));
const TransactionVerification = lazy(() => import("./components/transaction-verification/transaction-verification"));
const PaymentPage = lazy(() => import("./pages/payment"));
const TransactionComplete = lazy(() => import("./components/transaction-complete/transaction-complete.component"));
const PostTransactionDetails = lazy(() => import("./components/post-transaction-details/post-transaction-details"));
const CartPage = lazy(() => import("./pages/cart"));
const CartPaymentPage = lazy(() => import("./pages/cart-payment"));
const PaymentConfirmPage = lazy(() => import("./pages/payment-confirm"));
const ClaimPage = lazy(() => import("./pages/claim"));
const Suspicious = lazy(() => import('./components/suspicious/suspicious.component'));
const AccountVerification = lazy(() => import('./components/verification/account-verification'));

// Staff portal pages (Case Manager)
const CaseManagerDashboard = lazy(() => import("./pages/case-manager/CaseManagerDashboard"));

// Staff portal pages (Compliance)
const ComplianceDashboard = lazy(() => import("./pages/compliance/ComplianceDashboard"));

// Admin portal pages
const AdminTransactions = lazy(() => import("./pages/admin/transactions/transactions.component"));
const AdminTransaction = lazy(() => import("./pages/admin/transaction/transaction.component"));
const AdminUsers = lazy(() => import("./pages/admin/users/users.component"));
const AdminUser = lazy(() => import("./pages/admin/user/user.component"));
const AdminReports = lazy(() => import("./pages/admin/reports/reports.component"));
const AdminSurveys = lazy(() => import("./pages/admin/surveys/surveys.component"));
const AdminAllDiscounts = lazy(() => import("./pages/admin/all-discounts/all-discounts.component"));
const AdminCreateDiscount = lazy(() => import("./pages/admin/create-discount/create-discount.component"));
const AdminFedexLabels = lazy(() => import("./pages/admin/fedex-labels/fedex-labels.component"));
const AdminDashboard = lazy(() => import("./pages/admin/dashboard/dashboard.component"));
const TransactionReview = lazy(() => import("./pages/admin/transaction-review/transaction-review.component"));
const AdminNotifications = lazy(() => import("./pages/admin/notifications/notifications.component"));

// Loading fallback for lazy components
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div className="spinner" aria-label="Loading..." />
  </div>
);

// Dashboard redirect component - sends users to their role-appropriate dashboard
const DashboardRedirect = lazy(() => import("./components/dashboard-redirect/DashboardRedirect"));

const httpLink = new HttpLink({
  uri: apolloURL,
});

// Setup the header for the request
const middlewareAuthLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem("token");

  const authorizationTokenHeader = token ? `${token}` : null;
  operation.setContext({
    headers: {
      authorization: authorizationTokenHeader,
    },
  });
  return forward(operation);
});

//After the backend responds, we take the refreshToken from headers if it exists, and save it in the cookie.
const afterwareLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;

    if (headers) {
      const refreshToken = headers.get("x-esig-access-token");
      if (refreshToken) {
        localStorage.setItem("token", refreshToken);
      }
    }

    return response;
  });
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      if (
        message === "Access denied! You don't have permission for this action!"
      ) {
        window.location.replace("/login");
      }
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, extensions ${extensions}`
      );
    });

  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const client = new ApolloClient({
  link: from([errorLink, middlewareAuthLink, afterwareLink, httpLink]),
  cache: new InMemoryCache(),
  credentials: "include",
});

// Admin Apollo client — points to /admin endpoint which uses AdminResolver (all data, not user-scoped)
const adminHttpLink = new HttpLink({
  uri: adminApolloURL,
});

export const adminClient = new ApolloClient({
  link: from([errorLink, middlewareAuthLink, afterwareLink, adminHttpLink]),
  cache: new InMemoryCache(),
  credentials: "include",
});

// Create the HelloSign Embedded instance.
// Only do this once!
export const helloSignClient = new HelloSign({
  clientId: import.meta.env.VITE_HELLO_SIGN,
  skipDomainVerification: true,
});

/**
 * Portal App (LAYER-CODEBASE-ARCHITECTURE)
 *
 * Marketing pages have been moved to the marketing-site (Astro).
 * This portal now only handles:
 * - Authentication (login, register, password reset)
 * - Authenticated customer routes (accounts, transactions, payments)
 */
function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <ApolloProvider client={client}>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Authentication routes - no Shell wrapper
                      Intentionally minimal: users see a clean, focused login experience
                      without the portal navigation. After auth, they're redirected to
                      authenticated routes which include the full Shell with header/nav. */}
                  <Route path="login" element={<Login />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route path="reset-password" element={<ResetPassword />} />
                  <Route path="join" element={<Register />} />
                  {/* WorkOS AuthKit callback route */}
                  <Route path="auth/callback" element={<AuthCallback />} />
                  <Route path="register" element={<MainRegistrationPage />}>
                    <Route path="user-details" element={<UserInfo />} />
                    <Route path="personal-details" element={<PersonalDetails />} />
                    <Route path="confirmation" element={<Confirmation />} />
                  </Route>
                  <Route path="locked-user" element={<LockedPage />} />
                  <Route path="suspicious/:id" element={<Suspicious />} />

                  {/* All authenticated routes share a single Shell
                      Shell gets user role from auth context - shows ALL nav items user can access */}
                  <Route element={<Shell />}>
                    {/* Universal dashboard redirect - sends to role-appropriate dashboard */}
                    <Route path="dashboard" element={<DashboardRedirect />} />

                    {/* Customer routes - accessible to all authenticated users */}
                    <Route path="accounts">
                      <Route path="me" element={<Overview />} />
                      <Route path="details" element={<AccountDetails />} />
                      <Route path="transactions" element={<Navigate to="/accounts/me" replace />} />
                      <Route path="history" element={<Navigate to="/accounts/me" replace />} />
                      <Route path="billing" element={<BillingPage />} />
                    </Route>
                    {/* Transaction creation flow */}
                    <Route path="transactions">
                      <Route index element={<Navigate to="create/start" replace />} />
                      <Route path="create/start" element={<CreateTransaction />} />
                      <Route path=":transactionId/medallions" element={<CertificateType />} />
                      <Route path=":transactionId/shipping" element={<Checkout />} />
                      <Route path=":transactionId/review" element={<Checkout />} />
                      <Route path=":transactionId/verify" element={<TransactionVerification />} />
                      <Route path=":transactionId/payment" element={<Navigate to="/cart" replace />} />
                    </Route>
                    {/* Cart */}
                    <Route path="cart" element={<CartPage />} />
                    <Route path="cart/payment" element={<CartPaymentPage />} />
                    <Route path="payment/confirm" element={<PaymentConfirmPage />} />
                    {/* Post-transaction routes */}
                    <Route path="transactions/:transactionId/details" element={<PostTransactionDetails />} />
                    <Route path="transactions/:transactionId/completed" element={<TransactionComplete />} />

                    {/* Delegation: claim transaction via invitation link */}
                    <Route path="claim/:token" element={<ClaimPage />} />

                    <Route path="verify" element={<AccountVerification />} />

                    {/* Staff routes - Case Manager
                        Protected by STAFF access tier (Case Manager + Compliance + Admin)
                        Uses admin Apollo client to query /admin endpoint (all data, not user-scoped) */}
                    <Route
                      path="case-manager/*"
                      element={<RoleRoute accessTier="STAFF"><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="dashboard" element={<CaseManagerDashboard />} />
                      <Route index element={<Navigate to="dashboard" replace />} />
                    </Route>

                    {/* Staff routes - Compliance
                        Protected by STAFF access tier (Case Manager + Compliance + Admin)
                        Uses admin Apollo client to query /admin endpoint */}
                    <Route
                      path="compliance/*"
                      element={<RoleRoute accessTier="STAFF"><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="dashboard" element={<ComplianceDashboard />} />
                      <Route index element={<Navigate to="dashboard" replace />} />
                    </Route>

                    {/* Admin routes
                        Protected by ADMIN access tier (Admin + Super Admin only)
                        Uses admin Apollo client to query /admin endpoint */}
                    <Route
                      path="admin/*"
                      element={<RoleRoute accessTier="ADMIN"><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="transactions" element={<AdminTransactions />} />
                      <Route path="transaction/:id" element={<AdminTransaction />} />
                      <Route path="review/:id" element={<TransactionReview />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="user/:id" element={<AdminUser />} />
                      <Route path="reports" element={<AdminReports />} />
                      <Route path="surveys" element={<AdminSurveys />} />
                      <Route path="notifications" element={<AdminNotifications />} />
                      <Route path="discount-codes" element={<AdminAllDiscounts />} />
                      <Route path="create-discount" element={<AdminCreateDiscount />} />
                      <Route path="fedex-labels/:id" element={<AdminFedexLabels />} />
                      <Route index element={<Navigate to="dashboard" replace />} />
                    </Route>
                  </Route>

                  {/* Unauthorized page */}
                  <Route path="unauthorized" element={
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                      <h1>Unauthorized</h1>
                      <p>You do not have permission to access this page.</p>
                      <a href="/login">Return to Login</a>
                    </div>
                  } />

                  {/* Default redirect - send to login for authentication */}
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </ApolloProvider>
    </ThemeProvider>
  );
}

export default App;
